// src/view/LightweightChart.js

import React, { useEffect, useRef } from 'react';
import { createChart, CandlestickSeries, LineSeries, createSeriesMarkers } from 'lightweight-charts';
//## LineSeries 추가
const LightweightChart = ({ daily, result }) => {
    const chartContainerRef = useRef(null);
    const chartRef = useRef(null);
    const candlestickSeriesRef = useRef(null);
    const markersPluginRef = useRef(null);
    const averagePriceSeriesRef = useRef(null); //## 평균단가 라인 시리즈 추가

    useEffect(() => {
        // 차트가 이미 존재하면 정리
        if (chartRef.current) {
            chartRef.current.remove();
            chartRef.current = null;
        }

        // 컨테이너가 없으면 리턴
        if (!chartContainerRef.current || !daily || daily.length === 0 || !result || result.length === 0) {
            return;
        }

        // 차트 생성
        const chart = createChart(chartContainerRef.current, {
            width: chartContainerRef.current.clientWidth,
            height: 400,
            layout: {
                background: { 
                    type: 'solid', 
                    color: 'white' 
                },
                textColor: 'black',
            },
            grid: {
            vertLines: {
                color: 'rgba(197, 203, 206, 0.5)',
            },
            horzLines: {
                color: 'rgba(197, 203, 206, 0.5)',
            },
            },
            crosshair: {
                mode: 0, // Normal crosshair mode
            },
            rightPriceScale: {
                borderColor: 'rgba(197, 203, 206, 0.8)',
            },
            timeScale: {
                borderColor: 'rgba(197, 203, 206, 0.8)',
                timeVisible: true,
                secondsVisible: false,
                tickMarkFormatter: (time) => {
                    // time: { year, month, day } 또는 YYYY-MM-DD 문자열
                    // Lightweight Charts v5 기준
                    if (typeof time === 'string') {
                        // '2024-03-21' → '2024.3.21'
                        const [year, month, day] = time.split('-');
                        return `${year}.${parseInt(month)}.${parseInt(day)}`;
                    }
                    // 객체일 경우
                    if (typeof time === 'object' && time.year && time.month && time.day) {
                        return `${time.year}.${time.month}.${time.day}`;
                    }
                    return '';
                },
            },
            localization: {
                locale: 'ko-KR', // 언어 및 지역 설정
                dateFormat: 'yyyy.MM.dd', // 원하는 날짜 형식
                // timeFormatter: () => '',
            },
        });

        // v5.0 새로운 API: addSeries(SeriesType, options)
        const candlestickSeries = chart.addSeries(CandlestickSeries, {
            upColor: '#26a69a',
            downColor: '#ef5350',
            borderVisible: false,
            wickUpColor: '#26a69a',
            wickDownColor: '#ef5350',
            lastValueVisible: false,      // 마지막 값 마커 숨김
            priceLineVisible: false,      // 가격선(가로선) 숨김
        });

        //## 평균단가 라인 시리즈 추가
        const averagePriceSeries = chart.addSeries(LineSeries, {
            color: '#dc2626', // 붉은색
            lineWidth: 2,
            lineStyle: 1, // 점선 (0: Solid, 1: Dotted, 2: Dashed, 3: LargeDashed, 4: SparseDotted)
            lastValueVisible: false, // 마지막 값 라벨 숨김
            priceLineVisible: false, // 가격선 숨김
            crosshairMarkerVisible: true, // 크로스헤어 마커 표시
            crosshairMarkerRadius: 4, // 크로스헤어 마커 크기
            crosshairMarkerBorderColor: '#dc2626', // 크로스헤어 마커 테두리 색상
            crosshairMarkerBackgroundColor: '#dc2626', // 크로스헤어 마커 배경 색상
        });


        // daily 데이터를 캔들스틱 차트 형식으로 변환
        const candleData = daily.map(item => ({
            time: item.date, // 날짜 문자열 (YYYY-MM-DD 형식)
            open: item.open,
            high: item.high,
            low: item.low,
            close: item.close,
        }));

        //## 평균단가 데이터 준비 - result에서 priceAverage가 있는 거래만 필터링
        const priceAverageData = result
            .filter(trade => trade.priceAverage !== undefined && trade.priceAverage !== null)
            .map(trade => ({
                time: trade.date,
                value: parseFloat(trade.priceAverage)
            }));

        //## 캔들스틱 데이터 설정
        candlestickSeries.setData(candleData);
        
        //## 평균단가 라인 데이터 설정
        if (priceAverageData.length > 0) {
            averagePriceSeries.setData(priceAverageData);
        }

        // 캔들스틱 데이터 설정
        candlestickSeries.setData(candleData);

        // result 배열을 사용하여 매수/매도 마커 생성
        //if (result && result.length > 0) {
            const markers = result.map(trade => {
                const isBuy = trade.count > 0; // count가 양수면 매수, 음수면 매도
                
                return {
                    time: trade.date,
                    position: isBuy ? 'belowBar' : 'aboveBar',
                    color: isBuy ? '#2196F3' : '#e91e63',
                    shape: isBuy ? 'arrowUp' : 'arrowDown',
                    text: isBuy 
                    ? `매수 ${Math.abs(trade.count)}주 @ ${trade.price.toLocaleString()}`
                    : `매도 ${Math.abs(trade.count)}주 @ ${trade.price.toLocaleString()}`,
                    size: 1.2,
                };
            });

            // v5.0 새로운 플러그인 방식: createSeriesMarkers
            const markersPlugin = createSeriesMarkers(candlestickSeries, markers);
            markersPluginRef.current = markersPlugin;
        //}

        
        // 평균단가 점들을 별도 마커로 추가 (선택사항)
        /* if (priceAverageData.length > 0) {
            const avgPriceMarkers = priceAverageData.map(dataPoint => ({
                time: dataPoint.time,
                position: 'inBar', // 캔들 안에 표시
                color: '#dc2626', // 붉은색
                shape: 'circle',
                size: 0.8,
                text: `평균: ${dataPoint.value.toLocaleString()}`,
            }));

            // 평균단가 마커를 캔들스틱 시리즈에 추가
            const avgMarkersPlugin = createSeriesMarkers(candlestickSeries, [...markers, ...avgPriceMarkers]);
            markersPluginRef.current = avgMarkersPlugin; // 기존 마커와 합쳐서 사용
        } */

        // 차트 시간 스케일 자동 맞춤
        chart.timeScale().fitContent();

        // 리사이즈 핸들러
        const handleResize = () => {
            if (chartContainerRef.current && chart) {
                chart.applyOptions({ 
                    width: chartContainerRef.current.clientWidth 
                });
            }
        };

        window.addEventListener('resize', handleResize);

        // ref에 저장
        chartRef.current = chart;
        candlestickSeriesRef.current = candlestickSeries;
        averagePriceSeriesRef.current = averagePriceSeries; //## 평균단가 시리즈 저장

        // 클린업 함수
        return () => {
            window.removeEventListener('resize', handleResize);
            
            // 마커 플러그인 정리
            if (markersPluginRef.current) {
                markersPluginRef.current.detach();
                markersPluginRef.current = null;
            }

            //##
            if (averagePriceSeriesRef.current) {
                chart.removeSeries(averagePriceSeriesRef.current);
                averagePriceSeriesRef.current = null;
            }

            if (candlestickSeriesRef.current) {
                chart.removeSeries(candlestickSeriesRef.current);
                candlestickSeriesRef.current = null;
            }
            
            if (chartRef.current) {
                chartRef.current.remove();
                chartRef.current = null;
            }
        };
    }, [daily, result]);

    return (
        <div 
            ref={chartContainerRef}
            style={{ 
            width: '100%', 
            height: '400px',
            marginTop: '16px',
            }}
        />
    );
};

export default LightweightChart;
